import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Button,
  Card,
  Container,
  ListGroup,
  Modal,
  Row,
  Col,
  InputGroup,
} from "react-bootstrap";
import { PlusCircle, Trash, Pencil, Search, X } from "react-bootstrap-icons";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate, useLocation } from "react-router-dom";

const SearchableDropdown = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm("");
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <div className="position-relative" ref={dropdownRef}>
      <InputGroup onClick={() => setIsOpen(!isOpen)}>
        <Form.Control
          value={value || searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={placeholder || "Search options..."}
          onClick={(e) => e.stopPropagation()}
          onFocus={() => setIsOpen(true)}
        />
        <InputGroup.Text>
          {value ? (
            <X style={{ cursor: "pointer" }} onClick={clearSelection} />
          ) : (
            <Search />
          )}
        </InputGroup.Text>
      </InputGroup>

      {isOpen && (
        <div
          className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 z-index-dropdown"
          style={{ maxHeight: "200px", overflowY: "auto", zIndex: 1000 }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={index}
                className="p-2 hover-bg-light cursor-pointer"
                style={{
                  cursor: "pointer",
                  backgroundColor: value === option ? "#e9ecef" : "",
                }}
                onClick={() => handleSelect(option)}
              >
                {option}
              </div>
            ))
          ) : (
            <div className="p-2 text-muted">No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

const ExerciseComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [exercise, setExercise] = useState("");
  const [introText, setIntroText] = useState("");
  const [questions, setQuestions] = useState([]);
  const [options, setOptions] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [questionsByExercise, setQuestionsByExercise] = useState({});
  const [journey, setJourney] = useState(location.state?.journey || null);
  console.log("🚀 ~ SearchableDropdown ~ journey:", journey)
  const [modalState, setModalState] = useState({
    show: false,
    questionIndex: null,
    subQuestionIndex: null,
    subQuestionText: "",
    selectedResponse: "",
    selectedExercise: "",
    relatedQuestion: "",
    inputMethod: "direct",
  });

  // Generate time options for dropdown (1-30 seconds)
  const timeOptions = Array.from({ length: 30 }, (_, i) => i + 1);

  useEffect(() => {
    // Fetch exercises and questions from the first API endpoint
    axios
      .get(`${process.env.REACT_APP_BASE_URL}/futureSelf/get-exercises`)
      .then((response) => {
        // Extract exercises array
        const exerciseNames = response.data.exercises || [];
        setExercises(exerciseNames);

        // Organize questions by exercise
        const questions = response.data.questions || [];
        const questionMap = {};

        // Create a map of unique questions per exercise
        questions.forEach((q, index) => {
          // Use the index to determine which exercise this question belongs to
          const exerciseIndex = Math.min(index, exerciseNames.length - 1);
          const exercise = exerciseNames[exerciseIndex];

          if (exercise) {
            if (!questionMap[exercise]) {
              questionMap[exercise] = new Set();
            }
            questionMap[exercise].add(q.question);
          }
        });

        // Convert Sets to Arrays for each exercise
        const formattedQuestionMap = {};
        Object.keys(questionMap).forEach((exercise) => {
          formattedQuestionMap[exercise] = Array.from(questionMap[exercise]);
        });

        setQuestionsByExercise(formattedQuestionMap);

        // Get all unique questions for the options dropdown
        const uniqueOptions = [...new Set(questions.map((q) => q.question))];
        setOptions(uniqueOptions);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        toast.error("Error fetching exercises and questions");
      });

    // Also fetch questions from the second API endpoint
    axios
      .get(`${process.env.REACT_APP_BASE_URL}/questions/getQuestions`)
      .then((response) => {
        const data = response.data.data;
        const uniqueOptions = [...new Set(data.map((q) => q.question_text))];
        setOptions(uniqueOptions);
      })
      .catch(() => toast.error("Error fetching questions"));
  }, []);

  const handleAddQuestion = () => {
    if (!exercise.trim())
      return toast.error("Please enter an Exercise title first.");
    setQuestions((prev) => [
      ...prev,
      {
        question: "",
        prePrompt: "",
        postPrompt: "",
        selectedTime: 2,
        subQuestions: [],
      },
    ]);
  };

  const handleDeleteQuestion = (index) =>
    setQuestions((prev) => prev.filter((_, i) => i !== index));

  const handleTimeChange = (questionIndex, newTime) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i === questionIndex) {
          return { ...q, selectedTime: parseInt(newTime) };
        }
        return q;
      })
    );
  };

  const handleSubQuestionModal = (qIndex, subIndex = null) => {
    const subQuestion =
      subIndex !== null
        ? questions[qIndex].subQuestions[subIndex]
        : { subQuestion: "", response: "" };

    // Determine input method based on existing data (if editing)
    let inputMethod = "direct";
    if (
      subIndex !== null &&
      !subQuestion.subQuestion &&
      subQuestion.relatedQuestion
    ) {
      inputMethod = "related";
    }

    setModalState({
      show: true,
      questionIndex: qIndex,
      subQuestionIndex: subIndex,
      subQuestionText: subQuestion.subQuestion || "",
      selectedResponse: subQuestion.response || "",
      selectedExercise: subQuestion.selectedExercise || "",
      relatedQuestion: subQuestion.relatedQuestion || "",
      inputMethod: inputMethod,
    });
  };

  const handleSaveSubQuestion = () => {
    const {
      questionIndex,
      subQuestionIndex,
      subQuestionText,
      selectedResponse,
      selectedExercise,
      relatedQuestion,
      inputMethod,
    } = modalState;

    // Validate based on input method
    if (
      inputMethod === "direct" &&
      (!subQuestionText.trim() || !selectedResponse)
    ) {
      return toast.error("Enter Prompt and select Question.");
    }

    if (inputMethod === "related" && (!selectedExercise || !relatedQuestion)) {
      return toast.error("Select both Exercise and Related Question.");
    }

    setQuestions((prev) =>
      prev.map((q, idx) => {
        if (idx === questionIndex) {
          const updatedSubQuestions = [...q.subQuestions];

          // Create sub-question object based on input method
          const newSubQuestion =
            inputMethod === "direct"
              ? {
                  subQuestion: subQuestionText,
                  response: selectedResponse,
                  inputMethod: "direct",
                }
              : {
                  selectedExercise: selectedExercise,
                  relatedQuestion: relatedQuestion,
                  // Set the related question as the subQuestion text for display purposes
                  subQuestion: relatedQuestion,
                  // For related questions, we might need to fetch or set appropriate response
                  response: "Related question from " + selectedExercise,
                  inputMethod: "related",
                };

          if (subQuestionIndex !== null) {
            updatedSubQuestions[subQuestionIndex] = newSubQuestion;
          } else {
            updatedSubQuestions.push(newSubQuestion);
          }

          return { ...q, subQuestions: updatedSubQuestions };
        }
        return q;
      })
    );

    setModalState({
      show: false,
      questionIndex: null,
      subQuestionIndex: null,
      subQuestionText: "",
      selectedResponse: "",
      selectedExercise: "",
      relatedQuestion: "",
      inputMethod: "direct",
    });
  };

  const handleSaveExercise = async () => {
    // Validate exercise and questions
    if (!exercise.trim()) return toast.error("Exercise title is required.");

    // Validate that each question has text
    if (questions.some((q) => !q.question.trim())) {
      return toast.error("All questions must have text.");
    }
    console.log(journey, "journey===")
    // Prepare the payload with pre and post prompts and selected times
    const payload = {
      exercise,
      introText,
      journeyId: journey?.id,
      questions: questions.map((q) => ({
        question: q.question,
        prePrompt: q.prePrompt?.trim() || null,
        postPrompt: q.postPrompt?.trim() || null,
        selectedTime: q.selectedTime || 2, // Include the selected time
        subQuestions: q.subQuestions,
      })),
    };

    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/futureSelf/add-future-self-questionnaire`,
        payload
      );
      toast.success("Exercise saved successfully!");
      setTimeout(() => navigate("/journeys"), 2000);
    } catch (error) {
      console.error("Error saving exercise:", error);
      toast.error("Error saving exercise. Try again.");
    }
  };

  // Get filtered question options based on selected exercise
  const getFilteredQuestionOptions = () => {
    if (!modalState.selectedExercise) {
      return [];
    }
    return questionsByExercise[modalState.selectedExercise] || [];
  };

  // Handle exercise selection in modal
  const handleExerciseSelect = (exercise) => {
    setModalState({
      ...modalState,
      selectedExercise: exercise,
      relatedQuestion: "", // Reset related question when exercise changes
    });
  };

  // Handle related question selection
  const handleRelatedQuestionSelect = (question) => {
    setModalState({
      ...modalState,
      relatedQuestion: question,
    });
  };

  // Toggle between input methods
  const handleInputMethodChange = (method) => {
    setModalState({
      ...modalState,
      inputMethod: method,
    });
  };

  return (
    <Container className="py-4">
      <div className="cardHeader p-4">
      <div className="row align-items-center">
        <div className="col-auto">
          <button
            onClick={() => window.history.back()}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer"
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M29.3333 15.9998C29.3333 8.64784 23.3519 2.6665 15.9999 2.6665C8.64792 2.6665 2.66659 8.64784 2.66659 15.9998C2.66659 23.3518 8.64792 29.3332 15.9999 29.3332C23.3519 29.3332 29.3333 23.3518 29.3333 15.9998ZM10.1146 15.9998L17.7239 8.3905L19.6093 10.2758L13.8853 15.9998L19.6093 21.7238L17.7239 23.6092L10.1146 15.9998Z"
                fill="#27B99A"
              />
            </svg>
          </button>
        </div>
        <div className="col">
          <h2
            className="mb-0"
            style={{
              fontFamily: "Poppins",
              fontSize: "24px",
              fontWeight: 600,
            }}
          >
            Add Exercise
          </h2>
        </div>
      </div>
    </div>
      <Card className="mb-4 shadow-sm" style={{ height: "auto" }}>
        <Card.Body>
          <div className="row align-items-end">
            <div className="col-lg-6 col-md-6 col-12">
              <Form.Group className="text-start mb-3">
                <Form.Label className="text-dark">Exercise</Form.Label>
                <Form.Control
                  type="text"
                  value={exercise}
                  onChange={(e) => setExercise(e.target.value)}
                  placeholder="Enter exercise title"
                />
              </Form.Group>
              <Form.Group className="text-start">
                <Form.Label className="text-dark">Introduction Text</Form.Label>
                <Form.Control
                  as="textarea" 
                  rows={1}
                  value={introText}
                  onChange={(e) => setIntroText(e.target.value)}
                  placeholder="Enter introduction text"
                />
              </Form.Group>
            </div>
            <div className="col-lg-6 col-md-6 col-12">
              <div className="text-end">
                <button className="primaryBtn" onClick={handleAddQuestion}>
                  <PlusCircle
                    style={{ position: "relative", top: "-2px" }}
                    className="me-2"
                  />{" "}
                  Add Question
                </button>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {questions.map((q, index) => (
        <Card key={index} className="mb-3 shadow-sm" style={{ height: "auto" }}>
          <Card.Body>
              <div className="row">
                <div className="col-lg-6 col-12">
                  <Form.Group className="text-start">
                    <Form.Label className="text-dark">Question</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      value={q.question}
                      onChange={(e) =>
                        setQuestions((prev) =>
                          prev.map((q, i) =>
                            i === index ? { ...q, question: e.target.value } : q
                          )
                        )
                      }
                    />
                  </Form.Group>
                </div>
                <div className="col-lg-2 offset-lg-4 col-12">
                  <Form.Group className="text-start mb-0">
                    <Form.Label className="text-dark">
                      Time (seconds)
                    </Form.Label>
                    <Form.Select
                      value={q.selectedTime || 2}
                      onChange={(e) => handleTimeChange(index, e.target.value)}
                    >
                      {timeOptions.map((seconds) => (
                        <option key={seconds} value={seconds}>
                          {seconds} sec
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-lg-6 col-12">
                  <Form.Group className="text-start mb-3">
                    <Form.Label className="text-dark">Pre-Prompt</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      value={q.prePrompt || ""}
                      onChange={(e) =>
                        setQuestions((prev) =>
                          prev.map((q, i) =>
                            i === index
                              ? { ...q, prePrompt: e.target.value }
                              : q
                          )
                        )
                      }
                    />
                  </Form.Group>
                </div>
              </div>
              <div className="row align-items-end mb-3">
                <div className="col-lg-6 col-12">
                  <Form.Group className="text-start">
                    <Form.Label className="text-dark">Post-Prompt</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      value={q.postPrompt || ""}
                      onChange={(e) =>
                        setQuestions((prev) =>
                          prev.map((q, i) =>
                            i === index
                              ? { ...q, postPrompt: e.target.value }
                              : q
                          )
                        )
                      }
                    />
                  </Form.Group>
                </div>
                <div className="col-lg-6 col-12">
                  <div className="d-flex align-items-center justify-content-end gap-3 mt-3">
                    <button
                      className="primaryBtn"
                      onClick={() => handleSubQuestionModal(index)}
                    >
                      <PlusCircle
                        style={{ position: "relative", top: "-2px" }}
                        className="me-2 "
                      />{" "}
                      Add Prompt Section
                    </button>
                    <button
                      className="primaryBtn red"
                      onClick={() => handleDeleteQuestion(index)}
                    >
                      <Trash className="me-2" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            {q.subQuestions.length > 0 && (
              <ListGroup className="mt-3">
                {q.subQuestions.map((sub, subIndex) => (
                  <ListGroup.Item
                    key={subIndex}
                    className="d-flex justify-content-between align-items-center bg-light"
                  >
                    <div>
                      {sub.subQuestion}
                      <small className="text-muted">
                        {sub.inputMethod === "related"
                          ? ` (Related from: ${sub.selectedExercise})`
                          : ` (${sub.response})`}
                      </small>
                    </div>
                    <div>
                      <Button
                        variant="link"
                        onClick={() => handleSubQuestionModal(index, subIndex)}
                      >
                        <Pencil />
                      </Button>
                      <Button
                        variant="link"
                        className="text-danger"
                        onClick={() =>
                          setQuestions((prev) =>
                            prev.map((q, i) =>
                              i === index
                                ? {
                                    ...q,
                                    subQuestions: q.subQuestions.filter(
                                      (_, sIdx) => sIdx !== subIndex
                                    ),
                                  }
                                : q
                            )
                          )
                        }
                      >
                        <Trash />
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Card.Body>
        </Card>
      ))}

      {questions.length > 0 && (
        <button
          className="d-block ms-auto primaryBtn"
          onClick={handleSaveExercise}
        >
          Save Exercise
        </button>
      )}

      <Modal
        show={modalState.show}
        onHide={() => setModalState({ show: false })}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalState.subQuestionIndex !== null ? "Edit" : "Add"} Prompt
            Section
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Input Method Selection */}
          <Form.Group className="mb-3">
            <Form.Label>Input Method</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="direct-input"
                name="input-method"
                label="SignUp Questions"
                checked={modalState.inputMethod === "direct"}
                onChange={() => handleInputMethodChange("direct")}
              />
              <Form.Check
                type="radio"
                id="related-question"
                name="input-method"
                label="Exercise Question"
                checked={modalState.inputMethod === "related"}
                onChange={() => handleInputMethodChange("related")}
              />
            </div>
          </Form.Group>

          {/* Direct Input Fields */}
          {modalState.inputMethod === "direct" && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Add Prompt</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={1}
                  value={modalState.subQuestionText}
                  onChange={(e) =>
                    setModalState({
                      ...modalState,
                      subQuestionText: e.target.value,
                    })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Select Question</Form.Label>
                <SearchableDropdown
                  options={options}
                  value={modalState.selectedResponse}
                  onChange={(value) =>
                    setModalState({ ...modalState, selectedResponse: value })
                  }
                  placeholder="Search Questions..."
                />
              </Form.Group>
            </>
          )}

          {/* Related Question Fields */}
          {modalState.inputMethod === "related" && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Select Exercise</Form.Label>
                <SearchableDropdown
                  options={exercises}
                  value={modalState.selectedExercise}
                  onChange={handleExerciseSelect}
                  placeholder="Search exercises..."
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Select Related Question</Form.Label>
                <SearchableDropdown
                  options={getFilteredQuestionOptions()}
                  value={modalState.relatedQuestion}
                  onChange={handleRelatedQuestionSelect}
                  placeholder="Search related questions..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="primaryBtn red"
            onClick={() => setModalState({ show: false })}
          >
            Cancel
          </button>
          <button className="primaryBtn" onClick={handleSaveSubQuestion}>
            Save
          </button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ExerciseComponent;
